import React from "react";
import { Link } from "react-router-dom";
import { Wind, CalendarCheck, FileDown, BookOpen, ArrowRight } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { useContent } from "../App";

const TOOLS = [
    {
        id: "score", icon: Wind, title: "Breath Score Calculator",
        desc: "Answer 8 quick questions and get a 0–100 freshness score, plus the exact habits to fix first.",
        cta: "Take the 60-second check", to: "/tools/breath-score",
    },
    {
        id: "guide", icon: BookOpen, title: "The Science Behind Bad Breath",
        desc: "Our deep-dive: what sulfur compounds, tongue biofilm and saliva research actually mean for your routine.",
        cta: "Read the deep-dive", to: "/guides/halitosis-science",
    },
    {
        id: "tracker", icon: CalendarCheck, title: "7-Day Fresh-Breath Tracker (PDF)",
        desc: "Print it, stick it on the mirror, and tick the four daily habits. Most people notice a difference within a week.",
        cta: "Download the tracker", href: "/api/tracker.pdf",
    },
    {
        id: "checklist", icon: FileDown, title: "Fresh-Breath Checklist (PDF)",
        desc: "The one-page morning-to-night routine from our homepage — free to download, no email needed.",
        cta: "Download the checklist", href: "/api/checklist.pdf",
    },
];

const ToolsPage = () => {
    const content = useContent();
    return (
        <div data-testid="tools-page" className="min-h-screen bg-[#FDFCF8]">
            <Header />
            <main className="max-w-[760px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
                <Reveal>
                    <p className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] font-bold text-[#718370]">
                        <span className="h-px w-10 bg-[#9AB098]" />
                        Free tools & templates
                    </p>
                </Reveal>
                <Reveal delay={0.08}>
                    <h1 className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#1A1D1A]">
                        Original resources worth bookmarking
                    </h1>
                </Reveal>
                <Reveal delay={0.16}>
                    <p className="mt-4 text-sm sm:text-base leading-relaxed text-[#6D736D]">
                        Everything here is free, printable or shareable — built to be the page other health and wellness writers link to.
                    </p>
                </Reveal>
                <div className="mt-12 grid gap-5 sm:grid-cols-2">
                    {TOOLS.map((t, i) => {
                        const Icon = t.icon;
                        const inner = (
                            <>
                                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#DDE5DB] text-[#576856]">
                                    <Icon size={20} strokeWidth={1.6} />
                                </span>
                                <h2 className="mt-4 font-serif text-lg font-semibold text-[#1A1D1A]">{t.title}</h2>
                                <p className="mt-2 flex-1 text-xs sm:text-sm leading-relaxed text-[#6D736D]">{t.desc}</p>
                                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#576856]">
                                    {t.cta}
                                    <ArrowRight size={13} strokeWidth={2} className="transition-transform duration-300 group-hover:translate-x-1" />
                                </span>
                            </>
                        );
                        const cls = "group flex h-full flex-col rounded-[20px] border border-[#E4DFD3] bg-white p-6 transition-all duration-500 hover:-translate-y-1 hover:border-[#C4BCA8] hover:shadow-[0_20px_50px_-20px_rgba(34,37,34,0.25)]";
                        return (
                            <Reveal key={t.id} delay={i * 0.08} className="h-full">
                                {t.to ? (
                                    <Link to={t.to} data-testid={`tool-card-${t.id}`} className={cls}>{inner}</Link>
                                ) : (
                                    <a href={t.href} data-testid={`tool-card-${t.id}`} className={cls}>{inner}</a>
                                )}
                            </Reveal>
                        );
                    })}
                </div>
            </main>
            {content && <Footer footer={content.content.footer} />}
        </div>
    );
};

export default ToolsPage;
